import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import TextareaAutosize from "react-textarea-autosize";
import { observer } from "mobx-react";
import { Ionicons, Feather, MaterialIcons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import { DndContext } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSetDragKit } from "./useSetDragKit";
import Tooltip from "../../Components/Tooltip";
import FloatingMenu from "../../Components/FloatingMenu";
import Colors from "../../../Resources/Color";
import {
  BlockPlanificationConstants,
  WorkoutLimits,
} from "../../../Resources/Constants";
import {
  openFloatingSearch,
  closeFloatingSearch,
} from "../../Exercise/Search/GlobalExerciseSearchFloating";
import exerciseHistoryStore, {
  openExerciseHistory,
  updateExerciseHistoryAnchor,
} from "../../Exercise/History/ExerciseHistoryStore";
import Attribute from "../../Firestore/Attribute";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useScreenDimensions, useDevice } from "../../_layout";
import DuplicateExerciseModal from "./DuplicateExerciseModal";
import { InlineProtocolTextInput } from "../../Exercise/ProtocolTextInput";
import CustomAlert from "../../Components/CustomAlert";
import { hasProtocolTextError } from "../../utils/protocolTextUtils";
import { AntDesign } from "@expo/vector-icons";
import LinkSlotView from "../../Exercise/LinkSlotView";
import FloatingExerciseLink from "../../Components/FloatingExerciseLink";
import MobileScaledTextarea from "../../Components/MobileScaledTextarea";
import SetValueInput from "./SetValueInput";
import { ViewModeSetRow, EditModeSetRow } from "./SetRow";
import {
  CameraIconView,
  NonEditVideoDot,
  PriorityIconView,
  NonEditPriorityDot,
} from "./SetRowComponents";
import { useToast } from "../../contexts/ToastContext";
import {
  formatNumber1Dec,
  formatNumber2Dec,
  formatNumberWhole,
} from "../../utils/numberUtils";

// Enable drag-and-drop for set reordering
const ENABLE_SET_DRAGGING = true;

const ExerciseCard = observer(
  ({
    exercise,
    isEditMode,
    onPressCard,
    onCopyExercise,
    showExerciseHistory = true,
    onShowProtocolsGuide,
    onRequestEditFocus,
    focusField,
    focusSerieId,
    focusFromDirectClick = false,
    onFocusHandled,
    block: blockFromProps,
    dragHandleRef,
    dragHandleListeners,
    showSetPriority = false,
    rirOrRpe = false,
    exercisesExpanded = true,
  }) => {
    const { t, i18n } = useTranslation();
    const { isMobile, isIOS } = useDevice();
    const { screenSize } = useScreenDimensions();
    const { showToast } = useToast();
    const [isHovered, setIsHovered] = useState(false);
    const [isProtocolRowHovered, setIsProtocolRowHovered] = useState(false);
    const [isProtocolFocused, setIsProtocolFocused] = useState(false);
    const [hasProtocolBeenBlurred, setHasProtocolBeenBlurred] = useState(false);
    const [isNotesFocused, setIsNotesFocused] = useState(false);
    const notesInputRef = useRef(null);
    const protocolInputRef = useRef(null);
    const setInputRefs = useRef(new Map());
    const [isEditExerciseHovered, setIsEditExerciseHovered] = useState(false);
    const [isLinkHovered, setIsLinkHovered] = useState(false);
    const [isCopyExerciseHovered, setIsCopyExerciseHovered] = useState(false);
    const [isMoreHovered, setIsMoreHovered] = useState(false);
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [isProtocolInfoHovered, setIsProtocolInfoHovered] = useState(false);
    const [isCopySetsHovered, setIsCopySetsHovered] = useState(false);
    const [isPasteSetsHovered, setIsPasteSetsHovered] = useState(false);
    const [isCopyToNextHovered, setIsCopyToNextHovered] = useState(false);
    const [isDeleteSetHovered, setIsDeleteSetHovered] = useState(false);
    const [isReorderHovered, setIsReorderHovered] = useState(false);
    const [isAddSetHovered, setIsAddSetHovered] = useState(false);
    const [isAddNoteHovered, setIsAddNoteHovered] = useState(false);
    const [isDragIndicatorHovered, setIsDragIndicatorHovered] = useState(false);
    const [isHistoryIconHovered, setIsHistoryIconHovered] = useState(false);
    const [shouldDisableEditTooltip, setShouldDisableEditTooltip] =
      useState(false);
    const [
      isDuplicateExerciseModalVisible,
      setIsDuplicateExerciseModalVisible,
    ] = useState(false);
    const [showingSlotLinkView, setShowingSlotLinkView] = useState(false);
    const [showingDeleteSlotLinkView, setShowingDeleteSlotLinkView] =
      useState(false);
    const [showDeleteExerciseAlert, setShowDeleteExerciseAlert] =
      useState(false);
    const [showPasteSetsConfirmationAlert, setShowPasteSetsConfirmationAlert] =
      useState(false);
    const [isLinkFloatingOpen, setIsLinkFloatingOpen] = useState(false);
    const [shouldShowLinkIconTemporarily, setShouldShowLinkIconTemporarily] =
      useState(false);
    const historyAnchorRef = useRef(null);
    const sets = Array.isArray(exercise?.series) ? exercise.series : [];
    const displaySets = sets.length > 0 ? sets : [{ id: "placeholder" }];
    const notesValue = exercise?.notes ?? "";
    const isNotesExpanded =
      isNotesFocused ||
      (typeof notesValue === "string" && notesValue.trim() !== "");
    const isNotesEmpty = !(
      typeof notesValue === "string" && notesValue.trim() !== ""
    );

    const protocolSlot =
      typeof exercise?.protocolSlot === "string" &&
      exercise.protocolSlot.trim() !== ""
        ? exercise.protocolSlot
        : null;

    const protocolPairLabel = (() => {
      if (!protocolSlot) return null;
      const workoutExercises = Array.isArray(exercise?.workout?.exercises)
        ? exercise.workout.exercises
        : [];
      const hasPrimary = workoutExercises.some(
        (ex) =>
          ex?.protocolSlot === protocolSlot && ex?.isSecondaryProtocol === false
      );
      const hasSecondary = workoutExercises.some(
        (ex) =>
          ex?.protocolSlot === protocolSlot && ex?.isSecondaryProtocol === true
      );
      if (!hasPrimary || !hasSecondary) return null;
      return exercise?.isSecondaryProtocol ? "#2" : "#1";
    })();

    const exerciseDisplayName =
      typeof exercise?.getName === "function"
        ? exercise.getName(t, i18n.language)
        : exercise?.name || t("exercise-string");

    const moveCursorStyle = { cursor: "move" };

    // Mobile-specific dimensions for tighter layout
    const pillWidth = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_WIDTH
      : BlockPlanificationConstants.VALUE_PILL_WIDTH;
    const pillHeight = isMobile
      ? BlockPlanificationConstants.MOBILE_VALUE_PILL_HEIGHT
      : BlockPlanificationConstants.VALUE_PILL_HEIGHT;

    const block =
      blockFromProps ?? exercise?.workout?.block ?? exercise?.block ?? null;
    const coach = block?.client?.coach ?? block?.coach ?? null;
    // Access block.exercisesExpanded directly for MobX reactivity
    const isExercisesExpanded =
      block?.exercisesExpanded ?? exercisesExpanded ?? true;
    const shouldShowProtocolInfoButton = isEditMode && isProtocolFocused;
    const copiedSets = coach?.copiedSets ?? null;
    const pasteSetsDisabled = !copiedSets;
    const hasProtocolSlot =
      typeof exercise?.protocolSlot === "string" &&
      exercise.protocolSlot.trim() !== "";
    const historyEnabled = showExerciseHistory === true;
    const historyExercise = historyEnabled
      ? exerciseHistoryStore.exercise
      : null;
    const isHistoryOpen = historyEnabled ? exerciseHistoryStore.isOpen : false;
    const isHistoryActive =
      historyExercise?.id != null &&
      exercise?.id != null &&
      String(historyExercise.id) === String(exercise.id);
    const setHistoryAnchorNode = useCallback(
      (node) => {
        historyAnchorRef.current = node;
        if (!isHistoryOpen || !isHistoryActive) return;
        updateExerciseHistoryAnchor(exercise?.id, node);
      },
      [
        isHistoryOpen,
        isHistoryActive,
        exercise?.id,
        updateExerciseHistoryAnchor,
      ]
    );
    const shouldShowHistoryIcon =
      historyEnabled && isHistoryOpen && (isEditMode || isHistoryActive);
    const historyTooltipKey =
      isHistoryOpen && isHistoryActive
        ? "hideHistory-string"
        : "viewHistory-string";

    const getExerciseName = useCallback(
      (currentExercise) =>
        currentExercise?.getName?.(t, i18n.language) ||
        currentExercise?.name ||
        t("exercise-string"),
      [t, i18n.language]
    );

    const getWorkoutLabel = useCallback(
      (targetWorkout) => {
        if (!targetWorkout || !block?.workouts) return "";
        const week = targetWorkout?.week ?? 1;
        const customWeekName = block?.getWeekName?.(week) || "";
        const weekLabel =
          customWeekName.trim() !== ""
            ? customWeekName
            : t("weekWithNumber-string", { week });
        const sameWeekWorkouts = block.workouts
          .filter((w) => w.week === week)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const workoutIndex = sameWeekWorkouts.findIndex(
          (w) => w.id === targetWorkout.id
        );
        const workoutNumber =
          workoutIndex > -1
            ? workoutIndex + 1
            : (targetWorkout?.order ?? 0) + 1;
        const workoutName =
          typeof targetWorkout?.name === "string"
            ? targetWorkout.name.trim()
            : "";
        const workoutLabel =
          workoutName ||
          t("workoutWithNumber-string", {
            number: workoutNumber,
          });
        return `${weekLabel} · ${workoutLabel}`;
      },
      [block?.workouts, block?.getWeekName, t]
    );

    // Check if exercise has a link
    const hasLink =
      typeof exercise?.link === "string" && exercise.link.trim() !== "";

    // Show dot overlay only when there's a link
    const shouldShowLinkDot = hasLink;

    const moreMenuItems = [
      ...(historyEnabled
        ? [
            {
              key: "viewHistory",
              label: t("viewHistory-string"),
              icon: { type: "ionicons", name: "time-outline" },
            },
          ]
        : []),
      {
        key: "addVideoLink",
        label: hasLink
          ? t("editVideoLinkMenu-string")
          : t("addVideoLinkMenu-string"),
        icon: { type: "feather", name: "link" },
      },
      {
        key: "duplicateExercise",
        label: t("duplicateExerciseMenu-string"),
        icon: { type: "ionicons", name: "duplicate-outline" },
      },
      {
        key: "linkToSlot",
        label: hasProtocolSlot
          ? t("unlinkExerciseFromSlotMenu-string")
          : t("linkExerciseToSlotMenu-string"),
        icon: {
          type: "ionicons",
          name: hasProtocolSlot ? "unlink-outline" : "link-outline",
        },
      },
      {
        key: "deleteExercise",
        label: t("deleteExercise-string"),
        icon: { type: "ionicons", name: "trash-outline" },
        destructive: true,
      },
    ];

    const handleCopySets = (e) => {
      e?.stopPropagation?.();
      coach?.copySetsFromExercise?.(exercise);
      const exerciseName = getExerciseName(exercise);
      showToast("setsCopied-string", {
        type: "info",
        title: "setsCopied-string",
        description: "setsCopiedDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: { exerciseName },
      });
    };

    const handlePasteSets = (e) => {
      e?.stopPropagation?.();
      if (!copiedSets || !exercise) return;

      // Check if sets already have data
      if (exercise.hasSeriesWithAnyData) {
        setShowPasteSetsConfirmationAlert(true);
        return;
      }

      // If no data, paste directly
      exercise?.pasteCopiedSets?.(copiedSets);
      const exerciseName = getExerciseName(exercise);
      showToast("setsPasted-string", {
        type: "info",
        title: "setsPasted-string",
        description: "setsPastedDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: { exerciseName },
      });
    };

    const handleConfirmPasteSets = () => {
      if (!copiedSets || !exercise) return;
      setShowPasteSetsConfirmationAlert(false);
      exercise?.pasteCopiedSets?.(copiedSets);
      persistBlockChange();
      const exerciseName = getExerciseName(exercise);
      showToast("setsPasted-string", {
        type: "info",
        title: "setsPasted-string",
        description: "setsPastedDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: { exerciseName },
      });
    };

    const handleAddSet = () => {
      if (!exercise) return;

      // Check per-exercise set limit
      const currentSets = exercise?.series?.length || 0;
      if (currentSets >= WorkoutLimits.MAX_SETS_PER_EXERCISE) {
        showToast("maxSetsPerExerciseReached-string", {
          type: "warning",
          title: "maxSetsPerExerciseReached-string",
          description: "maxSetsPerExerciseDescription-string",
          variables: { limit: WorkoutLimits.MAX_SETS_PER_EXERCISE },
        });
        return;
      }

      exercise.addSerie();
    };

    const deleteSlot = () => {
      if (!block || !exercise) return;
      const slotLabel = exercise?.protocolSlot ? t(exercise.protocolSlot) : "";
      const workoutLabel = getWorkoutLabel(exercise?.workout);
      const exerciseName = getExerciseName(exercise);

      // Grab a reference to the live protocols array
      const blockProtocols = block.protocols;

      // Build a copy of all the ones you want to delete
      const matchingProtocols = blockProtocols.filter(
        (ex) =>
          ex.protocolSlot === exercise.protocolSlot &&
          ex.protocolWeek === exercise.getWeek &&
          (!exercise.isSecondaryProtocol || ex.isSecondaryProtocol)
      );

      // Remove each one by splicing it out of the original
      matchingProtocols.forEach((matching) => {
        const idx = blockProtocols.indexOf(matching);
        if (idx !== -1) {
          blockProtocols.splice(idx, 1);
        }
      });

      // Delete second exercise if it exists
      const secondExercise = exercise.getSecondProtocol;
      if (!exercise.isSecondaryProtocol && secondExercise) {
        secondExercise.setProperty("protocolSlot", null);
        secondExercise.setProperty("protocolWeek", null);
        secondExercise.setProperty("isSecondaryProtocol", false);
      }
      exercise.setProperty("protocolSlot", null);
      exercise.setProperty("protocolWeek", null);
      exercise.setProperty("isSecondaryProtocol", false);

      exercise.markChanged?.();
      persistBlockChange();

      showToast("exerciseUnlinkedToastTitle-string", {
        type: "info",
        title: "exerciseUnlinkedToastTitle-string",
        description: "exerciseUnlinkedToastDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: {
          exerciseName,
          slotName: slotLabel,
          workoutLabel,
        },
      });
    };

    const handleDeleteExercise = () => {
      if (!exercise || !block) return;
      // Use the reusable deletion function from Block
      block.deleteWorkoutExercise(exercise);
    };

    const [titleAnchorNode, setTitleAnchorNode] = useState(null);
    const titleAnchorNodeRef = useRef(null);
    const shouldAutoOpenExerciseSearchRef = useRef(false);

    const getSuggestionContextFromProtocolSlot = (protocolSlot) => {
      if (!protocolSlot) return null;
      const raw = String(protocolSlot).toLowerCase();
      if (raw.includes("squat")) return "squat";
      if (raw.includes("benchpress")) return "benchPress";
      if (raw.includes("deadlift")) return "deadlift";
      return null;
    };

    const getEditExerciseInstanceId = useCallback(() => {
      const blockId = block?.id || "unknown";
      const workoutId = exercise?.workout?.id || "unknown";
      const exerciseId = exercise?.id || "unknown";
      return `workout-exercise-search-${blockId}-${workoutId}-${exerciseId}`;
    }, [block?.id, exercise?.workout?.id, exercise?.id]);

    // Check if the exercise search is currently open for this exercise
    const isExerciseSearchOpen =
      coach?.activeFloatingSearchId === getEditExerciseInstanceId();

    const shouldShowEditSearchButton =
      isHovered || isMobile || isExerciseSearchOpen;

    const closeExerciseSearch = useCallback(() => {
      const instanceId = getEditExerciseInstanceId();
      closeFloatingSearch(coach, instanceId);
      // Keep tooltip disabled briefly after closing to prevent immediate re-show
      setShouldDisableEditTooltip(true);
      setTimeout(() => {
        setShouldDisableEditTooltip(false);
      }, 300);
    }, [coach, getEditExerciseInstanceId]);

    const requestAddExerciseFromEdit = useCallback(() => {
      const workout = exercise?.workout ?? null;
      workout?.setProperty?.("exerciseFocusRequest", {
        action: "addExercise",
      });
    }, [exercise?.workout]);

    const persistBlockChange = useCallback(() => {
      if (!block) return;
      if (block.client) {
        block.client.updateClientTransactionData(
          String(block.id).toLowerCase()
        );
        block.client.startOrResetBatchTimer();
      } else if (block.coach) {
        block.coach.updateCoachTransactionBlocks(
          String(block.id).toLowerCase()
        );
        block.coach.startOrResetBatchTimer();
      }
    }, [block]);

    const handleUpdateExerciseFromSearch = useCallback(
      (payload) => {
        if (!exercise || !payload) {
          closeExerciseSearch();
          return;
        }
        const keepOpen = payload?.keepOpen === true;

        const nextOriginalName =
          typeof payload.exerciseOriginalName === "string"
            ? payload.exerciseOriginalName
            : "";
        const nextName =
          typeof payload.exerciseName === "string" ? payload.exerciseName : "";

        if (nextOriginalName) {
          exercise.setProperty("originalName", nextOriginalName);
        }
        if (nextName) {
          exercise.setProperty("name", nextName);
        }

        if (Array.isArray(payload.modifiers)) {
          const EXCLUSIVE_TITLES = new Set([
            Attribute.AttributeTitles.equipment,
            Attribute.AttributeTitles.tempo,
            Attribute.AttributeTitles.rom,
            Attribute.AttributeTitles.stanceGrip,
          ]);

          const nextAttributes = [];
          const seenExclusive = new Set();

          payload.modifiers.forEach((modifier) => {
            if (!modifier) return;
            const originalName = String(modifier.originalName || "").trim();
            if (!originalName) return;

            const title = String(modifier.type || "").trim();
            const subtitle = modifier.subtype ? String(modifier.subtype) : "";

            if (title && EXCLUSIVE_TITLES.has(title)) {
              if (seenExclusive.has(title)) return;
              seenExclusive.add(title);
            }

            nextAttributes.push(
              new Attribute(
                undefined,
                originalName,
                title,
                subtitle,
                0,
                false,
                false,
                null,
                exercise
              )
            );
          });

          exercise.setProperty("attributes", nextAttributes);
        }

        exercise?.workout?.setProperty?.("hasChanged", true);
        persistBlockChange();
        if (keepOpen) {
          const workout = exercise?.workout ?? null;
          workout?.setProperty?.("exerciseFocusRequest", {
            action: "addExercise",
          });
        }
        closeExerciseSearch();
      },
      [exercise, persistBlockChange, closeExerciseSearch]
    );

    const openExerciseSearch = useCallback(() => {
      if (!coach?.setProperty) return;
      if (!exercise) return;

      const instanceId = getEditExerciseInstanceId();

      const initialText =
        typeof exercise?.originalName === "string" &&
        exercise.originalName.trim()
          ? exercise.getName(t, i18n.language)
          : exercise?.name || "";

      openFloatingSearch(coach, {
        instanceId,
        initialSearchText: initialText,
        referenceElement: titleAnchorNodeRef.current,
        onSelectExercise: handleUpdateExerciseFromSearch,
        onClose: closeExerciseSearch,
        onRequestAddExercise: requestAddExerciseFromEdit,
        suggestionContext: getSuggestionContextFromProtocolSlot(
          exercise?.protocolSlot
        ),
        crossAxisOffset: -24,
        slotExercise: exercise,
        primarySlotExercise: exercise,
        block,
        protocolSlot: exercise?.protocolSlot || null,
        currentWeek: exercise?.workout?.week ?? null,
        suggestionsMode: exercise?.protocolSlot ? "protocol" : "workout",
        currentWorkout: exercise?.workout,
        exerciseIndex:
          exercise?.workout?.exercises?.findIndex(
            (ex) => ex?.id === exercise?.id
          ) ?? 0,
        allowShiftEnterReopen: true,
      });

      if (!titleAnchorNodeRef.current) {
        shouldAutoOpenExerciseSearchRef.current = true;
      }
    }, [
      coach,
      exercise,
      block,
      t,
      i18n.language,
      closeExerciseSearch,
      getEditExerciseInstanceId,
      handleUpdateExerciseFromSearch,
    ]);

    useEffect(() => {
      if (!shouldAutoOpenExerciseSearchRef.current) return;
      if (!titleAnchorNodeRef.current) return;
      // Nothing else to do: openExerciseSearch already set coach state.
      shouldAutoOpenExerciseSearchRef.current = false;
    }, [titleAnchorNode]);

    const formatTargetValue = (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value;
      if (typeof value !== "number" || !Number.isFinite(value)) return "";
      if (value === 0) return "";
      return formatNumber1Dec(value);
    };

    const getBackgroundColor = (serie) => {
      if (!serie) return "rgba(0, 0, 0, 1)";

      const target = serie.getTargetRpeReference;
      const actual = serie.rpe;

      if (typeof target === "number" && target > 0.02 && actual > 0.02) {
        // round both to one decimal (to match your formatNumber logic)
        const a = parseFloat(formatNumber2Dec(actual));
        const t = parseFloat(formatNumber2Dec(target));

        if (Math.abs(a - t) >= 1) {
          return a > t ? "rgba(240, 68, 56, 1)" : "rgba(93, 156, 96, 1)";
        }
      }

      return Colors.darkgrayColor;
    };

    const formatActualSummary = (serie) => {
      if (!serie) return { weight: "", reps: "", rpe: "", hasRpe: false };

      const weight = serie.weight;
      const reps = serie.reps;
      const rpe = serie.rpe;

      const weightStr =
        typeof weight === "number" && Number.isFinite(weight) && weight !== 0
          ? formatNumber2Dec(weight)
          : "";
      const rpeStr =
        typeof rpe === "number" && Number.isFinite(rpe) && rpe !== 0
          ? formatNumber2Dec(rpe)
          : "";
      const hasOtherActual = weightStr !== "" || rpeStr !== "";
      const repsStr =
        typeof reps === "number" &&
        Number.isFinite(reps) &&
        (reps !== 0 || hasOtherActual)
          ? formatNumberWhole(reps)
          : "";

      return {
        weight: weightStr,
        reps: repsStr,
        rpe: rpeStr,
        hasRpe: rpeStr !== "",
      };
    };

    const formatActualTextForTooltip = (serie) => {
      if (!serie) return "";
      const summary = formatActualSummary(serie);
      if (!summary || (!summary.weight && !summary.reps && !summary.hasRpe)) {
        return "";
      }
      const parts = [];
      if (summary.weight) parts.push(summary.weight);
      if (summary.reps) parts.push(`x ${summary.reps}`);
      const weightRepsText = parts.join(" ");
      if (summary.hasRpe) {
        const rpeText = `${rirOrRpe ? "RIR" : "@"}${summary.rpe}`;
        return weightRepsText ? `${weightRepsText} ${rpeText}` : rpeText;
      }
      return weightRepsText;
    };

    const focusNotesInput = () => {
      try {
        const textarea = notesInputRef.current;
        if (textarea) {
          textarea.focus?.();
          // Move cursor to end of text
          if (textarea.setSelectionRange && typeof notesValue === "string") {
            const length = notesValue.length;
            textarea.setSelectionRange(length, length);
          }
        }
      } catch (e) {
        // no-op
      }
    };

    const openNotesEditor = () => {
      setIsNotesFocused(true);
      // iOS mobile: Use synchronous focus with cursor at end
      if (isMobile && isIOS) {
        // Focus will be handled by useLayoutEffect when isNotesFocused changes
        // Set cursor to end after a small delay to ensure textarea is focused
        setTimeout(() => {
          try {
            const textarea = notesInputRef.current;
            if (
              textarea &&
              textarea.setSelectionRange &&
              typeof notesValue === "string"
            ) {
              const length = notesValue.length;
              textarea.setSelectionRange(length, length);
            }
          } catch (e) {
            // no-op
          }
        }, 0);
        return;
      }
      // Desktop/other: Use setTimeout
      setTimeout(() => {
        focusNotesInput();
      }, 0);
    };

    const handleProtocolInfoMouseDown = (e) => {
      // On mobile, allow default behavior to blur the input
      if (isMobile) return;
      e?.preventDefault?.();
      e?.stopPropagation?.();
    };

    // iOS mobile: Use useLayoutEffect for synchronous focus (like SetValueInput)
    // Desktop/other: Use useEffect with setTimeout
    useLayoutEffect(() => {
      if (!isEditMode) return;
      if (!focusField || focusField !== "protocol") return;
      // If exercise doesn't have a protocol slot, clear focus request and return early
      if (!hasProtocolSlot) {
        onFocusHandled?.();
        return;
      }
      // Only use useLayoutEffect for iOS mobile
      if (!isMobile || !isIOS) return;
      // Skip autofocus on mobile unless it's a direct click on the protocol text
      if (!focusFromDirectClick) return;

      try {
        const textarea = protocolInputRef.current;
        if (textarea) {
          textarea.focus?.();
          // Move cursor to end of text
          if (
            textarea.setSelectionRange &&
            typeof exercise?.protocolText === "string"
          ) {
            const length = exercise.protocolText.length;
            textarea.setSelectionRange(length, length);
          }
        }
        onFocusHandled?.();
      } catch (e) {
        // no-op
      }
    }, [
      isEditMode,
      focusField,
      focusFromDirectClick,
      isMobile,
      isIOS,
      hasProtocolSlot,
      onFocusHandled,
    ]);

    // iOS mobile: Use useLayoutEffect for synchronous notes focus
    // Note: We don't set cursor position here to allow natural browser behavior when user clicks directly
    useLayoutEffect(() => {
      if (!isEditMode) return;
      // Only use useLayoutEffect for iOS mobile
      if (!isMobile || !isIOS) return;
      // Focus notes when isNotesFocused is set (from openNotesEditor)
      if (isNotesFocused && notesInputRef.current) {
        try {
          const textarea = notesInputRef.current;
          if (textarea) {
            textarea.focus?.();
            // Don't set cursor position - let browser handle it naturally
          }
        } catch (e) {
          // no-op
        }
      }
    }, [isEditMode, isNotesFocused, isMobile, isIOS]);

    // iOS mobile: Use useLayoutEffect for synchronous notes focus from focusField
    useLayoutEffect(() => {
      if (!isEditMode) return;
      if (!focusField || focusField !== "notes") return;
      // Only use useLayoutEffect for iOS mobile
      if (!isMobile || !isIOS) return;

      try {
        const textarea = notesInputRef.current;
        if (textarea) {
          textarea.focus?.();
          // Move cursor to end of text
          if (textarea.setSelectionRange && typeof notesValue === "string") {
            const length = notesValue.length;
            textarea.setSelectionRange(length, length);
          }
        }
        onFocusHandled?.();
      } catch (e) {
        // no-op
      }
    }, [isEditMode, focusField, isMobile, isIOS, notesValue, onFocusHandled]);

    useEffect(() => {
      if (!isEditMode) return;
      if (!focusField) return;

      if (focusField === "protocol") {
        // If exercise doesn't have a protocol slot, clear focus request and return early
        if (!hasProtocolSlot) {
          onFocusHandled?.();
          return;
        }
        // Skip useLayoutEffect path for iOS mobile (handled above)
        if (isMobile && isIOS && focusFromDirectClick) {
          return;
        }
        // Desktop/other: Use setTimeout
        // Skip autofocus on mobile unless it's a direct click on the protocol text
        if (!isMobile || focusFromDirectClick) {
          setTimeout(() => {
            try {
              const textarea = protocolInputRef.current;
              if (textarea) {
                textarea.focus?.();
                // Move cursor to end of text
                if (
                  textarea.setSelectionRange &&
                  typeof exercise?.protocolText === "string"
                ) {
                  const length = exercise.protocolText.length;
                  textarea.setSelectionRange(length, length);
                }
              }
              onFocusHandled?.();
            } catch (e) {
              // no-op
            }
          }, 0);
        } else {
          onFocusHandled?.();
        }
        return;
      }

      if (focusField === "notes") {
        // Skip useLayoutEffect path for iOS mobile (handled above)
        if (isMobile && isIOS) {
          return;
        }
        // Desktop/other: Use setTimeout
        setTimeout(() => {
          focusNotesInput();
          onFocusHandled?.();
        }, 0);
      }
    }, [
      isEditMode,
      focusField,
      focusSerieId,
      focusFromDirectClick,
      onFocusHandled,
      hasProtocolSlot,
    ]);

    // Clean up temporary link icon state when a link is added
    useEffect(() => {
      if (hasLink && shouldShowLinkIconTemporarily && !isEditMode) {
        // Link was added, no need for temporary state anymore
        setShouldShowLinkIconTemporarily(false);
      }
    }, [hasLink, shouldShowLinkIconTemporarily, isEditMode]);

    useEffect(() => {
      if (!isHistoryOpen || !isHistoryActive) return;
      if (!historyAnchorRef.current) return;
      updateExerciseHistoryAnchor(exercise?.id, historyAnchorRef.current);
    }, [isHistoryOpen, isHistoryActive, shouldShowHistoryIcon, exercise?.id]);

    useEffect(() => {
      return () => {
        updateExerciseHistoryAnchor(exercise?.id, null);
      };
    }, [exercise?.id]);

    // Memoized callbacks to prevent re-renders
    const handleMoveSet = useCallback(
      (dragIndex, hoverIndex) => {
        if (!exercise?.moveSerie) return;
        if (dragIndex === hoverIndex) return;
        exercise.moveSerie(dragIndex, hoverIndex);
      },
      [exercise]
    );

    // Use custom hook for dnd-kit logic (all platforms)
    const { sensors, handleDragStart, handleDragEnd } = useSetDragKit();

    // Create drag end handler with move logic
    const handleDndKitDragEnd = handleDragEnd(handleMoveSet, displaySets);

    // No-op handlers when dragging is disabled (DndContext requires handlers)
    const noOpDragStart = useCallback(() => {}, []);
    const noOpDragEnd = useCallback(() => {}, []);

    const handleDuplicateSerie = useCallback(
      (serie) => {
        exercise?.duplicateSerieBelow?.(serie);
      },
      [exercise]
    );

    const handleDeleteSerie = useCallback(
      (serie) => {
        exercise?.deleteSerie?.(serie);
      },
      [exercise]
    );

    const cardBody = (
      <View
        ref={setHistoryAnchorNode}
        collapsable={false}
        style={[
          styles.exerciseCard,
          isHovered && !isEditMode && styles.exerciseCardHovered,
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
        <View
          style={[
            styles.exerciseHeader,
            isHovered && styles.exerciseHeaderHovered,
          ]}
        >
          <View
            ref={(node) => {
              if (dragHandleRef) {
                if (typeof dragHandleRef === "function") {
                  dragHandleRef(node);
                } else if (dragHandleRef && "current" in dragHandleRef) {
                  dragHandleRef.current = node;
                }
              }
            }}
            style={[styles.exerciseMoveHandle, moveCursorStyle]}
            title={t("move-string")}
            onMouseEnter={() => !isMobile && setIsDragIndicatorHovered(true)}
            onMouseLeave={() => !isMobile && setIsDragIndicatorHovered(false)}
            {...dragHandleListeners}
          >
            <MaterialIcons
              name="drag-indicator"
              size={16}
              color={
                isDragIndicatorHovered
                  ? Colors.darkBubbleColor
                  : Colors.bubbleColor
              }
            />
          </View>

          <View
            ref={(node) => {
              const next = node || null;
              if (titleAnchorNodeRef.current === next) return;
              titleAnchorNodeRef.current = next;
              setTitleAnchorNode(next);
            }}
            style={[
              styles.exerciseTitleBlock,
              isEditMode && { userSelect: "text", cursor: "text" },
            ]}
          >
            <View
              style={[
                styles.exerciseNameRow,
                isEditMode && { userSelect: "text", cursor: "text" },
              ]}
            >
              <Text
                style={[
                  styles.exerciseName,
                  isEditMode && { userSelect: "text" },
                ]}
                numberOfLines={2}
              >
                {exerciseDisplayName}
              </Text>

              <Tooltip
                content={t("editExercise-string")}
                placement="top"
                disabled={isExerciseSearchOpen || shouldDisableEditTooltip}
              >
                <HoverableTouchableOpacity
                  style={[
                    styles.inlineEditButton,
                    !shouldShowEditSearchButton && { opacity: 0 },
                  ]}
                  hoverStyle={undefined}
                  pointerEvents={shouldShowEditSearchButton ? "auto" : "none"}
                  title={t("editExercise-string")}
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    // Disable tooltip when button is pressed
                    setShouldDisableEditTooltip(true);
                    openExerciseSearch();
                  }}
                  onMouseEnter={() =>
                    !isMobile && setIsEditExerciseHovered(true)
                  }
                  onMouseLeave={() =>
                    !isMobile && setIsEditExerciseHovered(false)
                  }
                >
                  <Feather
                    name="edit-2"
                    size={12}
                    color={Colors.grayColor}
                    style={{ opacity: isEditExerciseHovered ? 0.7 : 1 }}
                  />
                </HoverableTouchableOpacity>
              </Tooltip>
            </View>

            {protocolSlot && (
              <View
                style={[
                  styles.exerciseProtocolSlotRow,
                  isEditMode && { userSelect: "text", cursor: "text" },
                ]}
              >
                <Text
                  style={[
                    styles.exerciseProtocolSlotText,
                    isEditMode && {
                      userSelect: "text",
                      cursor: "text",
                    },
                  ]}
                  numberOfLines={1}
                >
                  {t(protocolSlot)}
                </Text>
                {protocolPairLabel && (
                  <Text style={styles.exerciseProtocolSlotBadge}>
                    {protocolPairLabel}
                  </Text>
                )}
              </View>
            )}
          </View>

          <View style={styles.exerciseHeaderActions}>
            {/* Show link icon: always in edit mode, only when has link in non-edit mode, or temporarily when opening add link dialog */}
            {shouldShowHistoryIcon ? (
              <View collapsable={false}>
                <Tooltip content={t(historyTooltipKey)} placement="top">
                  <HoverableTouchableOpacity
                    style={styles.iconButton}
                    hoverStyle={undefined}
                    title={t(historyTooltipKey)}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      openExerciseHistory({
                        exercise,
                        client:
                          block?.client ??
                          exercise?.workout?.block?.client ??
                          null,
                        anchorEl: historyAnchorRef.current,
                      });
                    }}
                    onMouseEnter={() =>
                      !isMobile && setIsHistoryIconHovered(true)
                    }
                    onMouseLeave={() =>
                      !isMobile && setIsHistoryIconHovered(false)
                    }
                  >
                    <Ionicons
                      name={isHistoryActive ? "time" : "time-outline"}
                      size={14}
                      color={
                        isHistoryIconHovered
                          ? Colors.darkBubbleColor
                          : Colors.bubbleColor
                      }
                    />
                  </HoverableTouchableOpacity>
                </Tooltip>
              </View>
            ) : (
              (isEditMode || hasLink || shouldShowLinkIconTemporarily) && (
                <FloatingExerciseLink
                  exercise={exercise}
                  isOpen={isLinkFloatingOpen}
                  onOpenChange={(open) => {
                    setIsLinkFloatingOpen(open);
                    // When closing, check if link exists - if not, hide temporary icon
                    if (!open) {
                      const currentHasLink =
                        typeof exercise?.link === "string" &&
                        exercise.link.trim() !== "";
                      if (!currentHasLink && !isEditMode) {
                        setShouldShowLinkIconTemporarily(false);
                      }
                    }
                  }}
                  onClose={() => {
                    setIsLinkFloatingOpen(false);
                    // When closing, check if link exists - if not, hide temporary icon
                    const currentHasLink =
                      typeof exercise?.link === "string" &&
                      exercise.link.trim() !== "";
                    if (!currentHasLink && !isEditMode) {
                      setShouldShowLinkIconTemporarily(false);
                    }
                  }}
                >
                  <Tooltip
                    content={
                      hasLink ? "editVideoLink-string" : "addVideoLink-string"
                    }
                    placement="top"
                    disabled={isLinkFloatingOpen}
                  >
                    <View
                      style={[styles.iconButton, styles.linkIconContainer]}
                      onMouseEnter={() => !isMobile && setIsLinkHovered(true)}
                      onMouseLeave={() => !isMobile && setIsLinkHovered(false)}
                    >
                      <Feather
                        name="link"
                        size={14}
                        color={
                          !isLinkHovered
                            ? Colors.bubbleColor
                            : Colors.darkBubbleColor
                        }
                      />
                      {shouldShowLinkDot && (
                        <View style={styles.linkDotOverlay} />
                      )}
                    </View>
                  </Tooltip>
                </FloatingExerciseLink>
              )
            )}

            <Tooltip content={t("copyExercise-string")} placement="top">
              <HoverableTouchableOpacity
                style={styles.iconButton}
                hoverStyle={undefined}
                title={t("copyExercise-string")}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onCopyExercise?.(exercise);
                  const exerciseName = getExerciseName(exercise);
                  showToast("exerciseCopied-string", {
                    type: "info",
                    title: "exerciseCopied-string",
                    description: "exerciseCopiedDescription-string",
                    duration: BlockPlanificationConstants.TOAST_DURATION_MS,
                    variables: { exerciseName },
                  });
                }}
                onMouseEnter={() => !isMobile && setIsCopyExerciseHovered(true)}
                onMouseLeave={() =>
                  !isMobile && setIsCopyExerciseHovered(false)
                }
              >
                <Ionicons
                  name="copy-outline"
                  size={14}
                  color={
                    isCopyExerciseHovered
                      ? Colors.darkBubbleColor
                      : Colors.bubbleColor
                  }
                />
              </HoverableTouchableOpacity>
            </Tooltip>

            <FloatingMenu
              placement="bottom-end"
              width={220}
              items={moreMenuItems}
              onOpenChange={setIsMoreMenuOpen}
              trigger={
                <Tooltip
                  content={t("moreOptions-string")}
                  placement="top"
                  disabled={isMoreMenuOpen}
                >
                  <View
                    style={styles.iconButton}
                    title={t("moreOptions-string")}
                    onMouseEnter={() => !isMobile && setIsMoreHovered(true)}
                    onMouseLeave={() => !isMobile && setIsMoreHovered(false)}
                  >
                    <Ionicons
                      name="ellipsis-horizontal"
                      size={16}
                      color={
                        isMoreHovered
                          ? Colors.darkBubbleColor
                          : Colors.bubbleColor
                      }
                    />
                  </View>
                </Tooltip>
              }
              renderItem={({ item, close }) => {
                const isDestructive = item?.destructive === true;
                const iconColor = isDestructive
                  ? Colors.redColor
                  : Colors.darkgrayColor;
                const textColor = isDestructive
                  ? Colors.redColor
                  : Colors.blackColor;

                const renderMenuIcon = () => {
                  if (item?.icon?.type === "feather") {
                    return (
                      <Feather
                        name={item.icon.name}
                        size={14}
                        color={iconColor}
                      />
                    );
                  }
                  if (item?.icon?.type === "ionicons") {
                    return (
                      <Ionicons
                        name={item.icon.name}
                        size={14}
                        color={iconColor}
                      />
                    );
                  }
                  return null;
                };

                return (
                  <TouchableOpacity
                    style={styles.moreMenuItemRow}
                    activeOpacity={0.8}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      if (item.key === "viewHistory") {
                        openExerciseHistory({
                          exercise,
                          client:
                            block?.client ??
                            exercise?.workout?.block?.client ??
                            null,
                          anchorEl: historyAnchorRef.current,
                        });
                      } else if (item.key === "duplicateExercise") {
                        setIsDuplicateExerciseModalVisible(true);
                      } else if (item.key === "addVideoLink") {
                        // If not in edit mode and no link, show icon temporarily for FloatingExerciseLink to anchor to
                        if (!isEditMode && !hasLink) {
                          setShouldShowLinkIconTemporarily(true);
                        }
                        setIsLinkFloatingOpen(true);
                      } else if (item.key === "linkToSlot") {
                        if (hasProtocolSlot) {
                          setShowingDeleteSlotLinkView(true);
                        } else {
                          setShowingSlotLinkView(true);
                        }
                      } else if (item.key === "deleteExercise") {
                        setShowDeleteExerciseAlert(true);
                      }
                      setTimeout(() => {
                        close();
                      }, 0);
                    }}
                  >
                    {renderMenuIcon()}
                    <Text
                      style={[styles.moreMenuItemText, { color: textColor }]}
                    >
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>

        {isExercisesExpanded &&
          (isEditMode
            ? hasProtocolSlot && (
                <View style={styles.exerciseInlineFieldRow}>
                  <View style={styles.exerciseInlineFieldAccent} />
                  <View style={styles.exerciseInlineFieldBody}>
                    <View
                      style={{ flexDirection: "row", alignItems: "center" }}
                      onMouseEnter={() =>
                        !isMobile && setIsProtocolRowHovered(true)
                      }
                      onMouseLeave={() =>
                        !isMobile && setIsProtocolRowHovered(false)
                      }
                    >
                      <InlineProtocolTextInput
                        exercise={exercise}
                        block={block}
                        inputRef={protocolInputRef}
                        showTypingAnimation={true}
                        onShowProtocolsGuide={onShowProtocolsGuide}
                        onFocus={() => setIsProtocolFocused(true)}
                        onBlur={() => {
                          setIsProtocolFocused(false);
                          setHasProtocolBeenBlurred(true);
                        }}
                        style={{ flex: 1 }}
                        inputStyle={styles.exerciseInlineTextInput}
                      />

                      {/* Show error icon when there's an error after blur, otherwise show info icon when focused */}
                      {hasProtocolBeenBlurred &&
                      !isProtocolFocused &&
                      hasProtocolTextError(exercise?.protocolText) ? (
                        <HoverableTouchableOpacity
                          style={styles.inlineEditButton}
                          hoverStyle={undefined}
                          title={t("protocolError-string")}
                          onMouseDown={handleProtocolInfoMouseDown}
                          onPress={(e) => {
                            e?.stopPropagation?.();
                            // On mobile, blur the input when clicking the info icon
                            if (isMobile && protocolInputRef.current) {
                              protocolInputRef.current.blur?.();
                            }
                            onShowProtocolsGuide?.();
                          }}
                          onMouseEnter={() =>
                            !isMobile && setIsProtocolInfoHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsProtocolInfoHovered(false)
                          }
                        >
                          <Ionicons
                            name="alert-circle-outline"
                            size={14}
                            color="red"
                            style={{
                              opacity: isProtocolInfoHovered ? 0.7 : 1,
                            }}
                          />
                        </HoverableTouchableOpacity>
                      ) : (
                        <HoverableTouchableOpacity
                          style={[
                            styles.inlineEditButton,
                            !shouldShowProtocolInfoButton && { opacity: 0 },
                          ]}
                          hoverStyle={undefined}
                          pointerEvents={
                            shouldShowProtocolInfoButton ? "auto" : "none"
                          }
                          title={t("protocolsGuide-string")}
                          onMouseDown={handleProtocolInfoMouseDown}
                          onPress={(e) => {
                            e?.stopPropagation?.();
                            // On mobile, blur the input when clicking the info icon
                            if (isMobile && protocolInputRef.current) {
                              protocolInputRef.current.blur?.();
                            }
                            onShowProtocolsGuide?.();
                          }}
                          onMouseEnter={() =>
                            !isMobile && setIsProtocolInfoHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsProtocolInfoHovered(false)
                          }
                        >
                          <Ionicons
                            name="information-circle-outline"
                            size={14}
                            color={Colors.grayColor}
                            style={{ opacity: isProtocolInfoHovered ? 0.7 : 1 }}
                          />
                        </HoverableTouchableOpacity>
                      )}
                    </View>
                  </View>
                </View>
              )
            : hasProtocolSlot &&
              typeof exercise?.protocolText === "string" &&
              exercise.protocolText.trim() !== "" && (
                <TouchableOpacity
                  style={styles.exerciseInlineFieldRow}
                  activeOpacity={0.9}
                  title={t("editProtocol-string")}
                  onPress={() => onRequestEditFocus?.("protocol")}
                >
                  <View style={styles.exerciseInlineFieldAccent} />
                  <View
                    style={[
                      styles.exerciseInlineFieldBody,
                      { flexDirection: "row", alignItems: "center" },
                    ]}
                  >
                    <Text
                      style={[
                        styles.exerciseInlineText,
                        { flex: 1 },
                        isMobile && { fontSize: 13 },
                      ]}
                    >
                      {exercise.protocolText}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}

        {isExercisesExpanded && (
          <>
            {/* 
            UX PROPOSAL: Camera Icon Column for Video Requests
          
          Layout Structure:
          ┌─────────────────────────────────────────────────────────────┐
          │ [CAM] │ WEIGHT │ REPS │ RPE │ [ACTUAL] (non-edit) or [ACTIONS] (edit) │
          └─────────────────────────────────────────────────────────────┘
          
          CAMERA ICON COLUMN (Leftmost):
          - Fixed width: ~24px (matches icon size + padding)
          - EDIT MODE: Always visible (even when no video state) - allows setting requireVideo
          - NON-EDIT MODE: Only visible when serie.requireVideo === true OR serie.hasVideo === true
          
          EDIT MODE Behavior (ALWAYS SHOW ICON):
          - Interactive camera icon (same as SerieRowView VideoView):
            * serie.hasVideo: Shows filled "videocam" icon (black)
              → Click: Opens video player (if coach online)
            * serie.requireVideo (and !hasVideo): Shows outline "videocam-outline" icon (black)
              → Click: Toggles requireVideo to false
            * Neither (no requireVideo, no hasVideo): Shows "videocam-off-outline" icon (gray/light)
              → Click: Sets requireVideo to true
          - Tooltip on hover:
            * hasVideo: No tooltip (or "View video")
            * requireVideo: "dontRequestVideo-string"
            * Neither: "requestVideo-string"
          - Hover state: Icon opacity reduces to 0.5 on hover
          
          NON-EDIT MODE Behavior (ONLY SHOW WHEN VIDEO STATE EXISTS):
          - Static camera icon display (only when requireVideo OR hasVideo):
            * serie.hasVideo: Shows filled "videocam" icon (black)
              → Click: Opens video player (if coach online)
            * serie.requireVideo: Shows outline "videocam-outline" icon (black)
              → Click: No action (or could trigger edit mode focus)
            * Neither: No icon shown (empty spacer maintains alignment)
          - Tooltip: Same as edit mode (when icon is visible)
          
          Visual States:
          - Icon size: 16px (consistent with other icons in ExerciseCard)
          - Icon color: 
            * hasVideo OR requireVideo: Colors.blackColor (or "black")
            * Neither: N/A (not shown)
          - Alignment: Vertically centered with set row
          - Spacing: 4-6px gap between camera column and WEIGHT column
          
          Layout Consistency:
          - Header row: Add empty spacer (same width as camera column) to maintain alignment
          - Both edit and non-edit modes: Same camera column width
          - Set rows: Camera icon aligns vertically with each set
          - When no sets have video: Column still exists but is invisible (maintains table structure)
          
          Implementation Notes:
          - Use conditional rendering: Only render icon container when serie.requireVideo || serie.hasVideo
          - Use same icon logic as SerieRowView VideoView component
          - Maintain gap spacing between columns (currently 8px gap in tableHeaderRow)
          - Camera column should be first element in setRow flex container
        */}
            <View style={[styles.setsTable, isMobile && { gap: 3 }]}>
              <View style={[styles.tableHeaderRow, isMobile && { gap: 6 }]}>
                {isEditMode ? (
                  <View
                    style={[
                      styles.tableHeaderCameraSpacer,
                      isMobile && { width: 20 },
                    ]}
                  />
                ) : (
                  <View
                    style={[
                      styles.tableHeaderCameraCell,
                      isMobile && { width: 20 },
                    ]}
                  >
                    <Ionicons
                      name="videocam-outline"
                      size={isMobile ? 11 : 12}
                      color={Colors.grayColor}
                    />
                  </View>
                )}
                {showSetPriority &&
                  (isEditMode ? (
                    <View
                      style={[
                        styles.tableHeaderPrioritySpacer,
                        isMobile && { width: 20 },
                      ]}
                    />
                  ) : (
                    <View
                      style={[
                        styles.tableHeaderPriorityCell,
                        isMobile && { width: 20 },
                      ]}
                    >
                      <Text style={styles.tableHeaderPriorityText}>
                        {t("priorityHeader-string")}
                      </Text>
                    </View>
                  ))}
                <Text style={[styles.tableHeaderText, { width: pillWidth }]}>
                  {t("weight-string")}
                </Text>
                <Text style={[styles.tableHeaderText, { width: pillWidth }]}>
                  {t("reps-string")}
                </Text>
                <Text style={[styles.tableHeaderText, { width: pillWidth }]}>
                  {rirOrRpe ? t("rir-string") : t("rpe-string")}
                </Text>
                {!isEditMode && (
                  <View
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      //   paddingRight: 6,
                    }}
                  >
                    {/* Copy/paste targets (WEIGHT/REPS/RPE) + protocolText. Does not include ACTUAL. */}
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: isMobile ? 4 : 2,
                      }}
                    >
                      <Tooltip content={t("copySets-string")} placement="top">
                        <HoverableTouchableOpacity
                          style={[
                            styles.inlineEditButton,
                            !shouldShowEditSearchButton && { opacity: 0 },
                          ]}
                          hoverStyle={undefined}
                          pointerEvents={
                            shouldShowEditSearchButton ? "auto" : "none"
                          }
                          onPress={(e) => {
                            handleCopySets(e);
                          }}
                          onMouseEnter={() =>
                            !isMobile && setIsCopySetsHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsCopySetsHovered(false)
                          }
                        >
                          <Ionicons
                            name="copy-outline"
                            size={12}
                            color={Colors.grayColor}
                            style={{ opacity: isCopySetsHovered ? 0.7 : 1 }}
                          />
                        </HoverableTouchableOpacity>
                      </Tooltip>

                      <Tooltip content={t("pasteSets-string")} placement="top">
                        <HoverableTouchableOpacity
                          style={[
                            styles.inlineEditButton,
                            pasteSetsDisabled && { opacity: 0.7 },
                            !shouldShowEditSearchButton && { opacity: 0 },
                          ]}
                          hoverStyle={undefined}
                          pointerEvents={
                            shouldShowEditSearchButton ? "auto" : "none"
                          }
                          onPress={(e) => {
                            handlePasteSets(e);
                          }}
                          onMouseEnter={() =>
                            !isMobile && setIsPasteSetsHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsPasteSetsHovered(false)
                          }
                        >
                          <Ionicons
                            name="clipboard-outline"
                            size={12}
                            color={
                              pasteSetsDisabled
                                ? Colors.borderColor
                                : Colors.grayColor
                            }
                            style={{ opacity: isPasteSetsHovered ? 0.7 : 1 }}
                          />
                        </HoverableTouchableOpacity>
                      </Tooltip>
                    </View>

                    <View
                      style={{
                        width: 1,
                        height: 12,
                        backgroundColor: Colors.borderColor,
                        marginRight: isMobile ? 6 : 8,
                        marginLeft: isMobile ? 0 : 4,
                        opacity: shouldShowEditSearchButton ? 1 : 0,
                      }}
                    />

                    <Text style={styles.tableHeaderActualText}>
                      {t("actual-string")}
                    </Text>
                  </View>
                )}

                {isEditMode && (
                  <View
                    style={[
                      styles.tableActionsSpacer,
                      {
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: isMobile ? 4 : 2,
                        width: isMobile ? 36 : 42,
                        marginRight: isMobile ? 4 : 0,
                      },
                    ]}
                  >
                    <Tooltip content={t("copySets-string")} placement="top">
                      <HoverableTouchableOpacity
                        style={[
                          styles.inlineEditButton,
                          !shouldShowEditSearchButton && { opacity: 0 },
                        ]}
                        hoverStyle={undefined}
                        pointerEvents={
                          shouldShowEditSearchButton ? "auto" : "none"
                        }
                        onPress={(e) => {
                          handleCopySets(e);
                        }}
                        onMouseEnter={() => setIsCopySetsHovered(true)}
                        onMouseLeave={() => setIsCopySetsHovered(false)}
                      >
                        <Ionicons
                          name="copy-outline"
                          size={12}
                          color={Colors.grayColor}
                          style={{ opacity: isCopySetsHovered ? 0.7 : 1 }}
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>

                    <Tooltip content={t("pasteSets-string")} placement="top">
                      <HoverableTouchableOpacity
                        style={[
                          styles.inlineEditButton,
                          pasteSetsDisabled && { opacity: 0.7 },
                          !shouldShowEditSearchButton && { opacity: 0 },
                        ]}
                        hoverStyle={undefined}
                        pointerEvents={
                          shouldShowEditSearchButton ? "auto" : "none"
                        }
                        onPress={(e) => {
                          handlePasteSets(e);
                        }}
                        title={t("pasteSets-string")}
                        // disabled={pasteSetsDisabled}
                        onMouseEnter={() => setIsPasteSetsHovered(true)}
                        onMouseLeave={() => setIsPasteSetsHovered(false)}
                      >
                        <Ionicons
                          name="clipboard-outline"
                          size={12}
                          color={Colors.grayColor}
                          style={{ opacity: isPasteSetsHovered ? 0.7 : 1 }}
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>
                  </View>
                )}
              </View>

              {isEditMode ? (
                <DndContext
                  sensors={sensors}
                  onDragStart={
                    ENABLE_SET_DRAGGING ? handleDragStart : noOpDragStart
                  }
                  onDragEnd={
                    ENABLE_SET_DRAGGING ? handleDndKitDragEnd : noOpDragEnd
                  }
                  autoScroll={false}
                >
                  <SortableContext
                    items={displaySets.map((s) => s?.id).filter(Boolean)}
                    strategy={verticalListSortingStrategy}
                  >
                    {displaySets.map((setItem, idx) => {
                      return (
                        <EditModeSetRow
                          key={setItem?.id ?? idx}
                          setItem={setItem}
                          idx={idx}
                          onMoveSet={handleMoveSet}
                          onDuplicateSerie={handleDuplicateSerie}
                          onDeleteSerie={handleDeleteSerie}
                          moveCursorStyle={moveCursorStyle}
                        >
                          <CameraIconView
                            serie={setItem}
                            block={block}
                            isEditMode={isEditMode}
                            persistBlockChange={persistBlockChange}
                          />

                          {showSetPriority && (
                            <PriorityIconView
                              serie={setItem}
                              block={block}
                              isEditMode={isEditMode}
                              persistBlockChange={persistBlockChange}
                            />
                          )}

                          <SetValueInput
                            setItem={setItem}
                            propertyName="targetWeight"
                            setInputRefs={setInputRefs}
                            autoFocusKey={`${
                              setItem?.id ?? "placeholder"
                            }:targetWeight`}
                            shouldAutoFocus={
                              focusField === "targetWeight" &&
                              focusSerieId === setItem?.id
                            }
                            onAutoFocusHandled={onFocusHandled}
                          />

                          <SetValueInput
                            setItem={setItem}
                            propertyName="targetReps"
                            setInputRefs={setInputRefs}
                            autoFocusKey={`${
                              setItem?.id ?? "placeholder"
                            }:targetReps`}
                            shouldAutoFocus={
                              focusField === "targetReps" &&
                              focusSerieId === setItem?.id
                            }
                            onAutoFocusHandled={onFocusHandled}
                          />

                          <SetValueInput
                            setItem={setItem}
                            propertyName="targetRpe"
                            setInputRefs={setInputRefs}
                            autoFocusKey={`${
                              setItem?.id ?? "placeholder"
                            }:targetRpe`}
                            shouldAutoFocus={
                              focusField === "targetRpe" &&
                              focusSerieId === setItem?.id
                            }
                            onAutoFocusHandled={onFocusHandled}
                          />
                        </EditModeSetRow>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              ) : (
                displaySets.map((setItem, idx) => (
                  <ViewModeSetRow key={setItem?.id ?? idx}>
                    <NonEditVideoDot serie={setItem} />

                    {showSetPriority && <NonEditPriorityDot serie={setItem} />}

                    <TouchableOpacity
                      style={[
                        styles.valuePill,
                        styles.valuePillViewMode,
                        { width: pillWidth, height: pillHeight },
                      ]}
                      activeOpacity={0.9}
                      title={t("editTargetWeight-string")}
                      onPress={() => {
                        if (setItem?.id === "placeholder") return;
                        onRequestEditFocus?.("targetWeight", setItem?.id);
                      }}
                    >
                      <Text
                        style={[
                          styles.valueText,
                          isMobile && styles.valueTextMobile,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {formatTargetValue(setItem?.targetWeight)}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.valuePill,
                        styles.valuePillViewMode,
                        { width: pillWidth, height: pillHeight },
                      ]}
                      activeOpacity={0.9}
                      title={t("editTargetReps-string")}
                      onPress={() => {
                        if (setItem?.id === "placeholder") return;
                        onRequestEditFocus?.("targetReps", setItem?.id);
                      }}
                    >
                      <Text
                        style={[
                          styles.valueText,
                          isMobile && styles.valueTextMobile,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {formatTargetValue(setItem?.targetReps)}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.valuePill,
                        styles.valuePillViewMode,
                        { width: pillWidth, height: pillHeight },
                      ]}
                      activeOpacity={0.9}
                      title={t("editTargetRPE-string")}
                      onPress={() => {
                        if (setItem?.id === "placeholder") return;
                        onRequestEditFocus?.("targetRpe", setItem?.id);
                      }}
                    >
                      <Text
                        style={[
                          styles.valueText,
                          isMobile && styles.valueTextMobile,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {formatTargetValue(setItem?.targetRpe)}
                      </Text>
                    </TouchableOpacity>

                    {(() => {
                      if (setItem?.id === "placeholder")
                        return <View style={styles.actualCell} />;
                      const summary = formatActualSummary(setItem);
                      if (
                        !summary ||
                        (!summary.weight && !summary.reps && !summary.hasRpe)
                      ) {
                        return <View style={styles.actualCell} />;
                      }
                      const parts = [];
                      if (summary.weight) parts.push(summary.weight);
                      if (summary.reps) parts.push(`x ${summary.reps}`);
                      const weightRepsText = parts.join(" ");
                      const tooltipText = formatActualTextForTooltip(setItem);
                      return (
                        <Tooltip
                          content={tooltipText}
                          placement="top"
                          delayedOpen={true}
                          disabled={!tooltipText}
                          rawText={true}
                        >
                          <View style={styles.actualCell}>
                            <Text
                              style={[
                                styles.actualText,
                                isMobile && styles.actualTextMobile,
                              ]}
                              numberOfLines={1}
                            >
                              {weightRepsText}
                              {summary.hasRpe && (
                                <>
                                  {" "}
                                  <Text
                                    style={[
                                      styles.actualText,
                                      isMobile && styles.actualTextMobile,
                                      { color: getBackgroundColor(setItem) },
                                    ]}
                                  >
                                    {rirOrRpe ? "RIR" : "@"}
                                    {summary.rpe}
                                  </Text>
                                </>
                              )}
                            </Text>
                          </View>
                        </Tooltip>
                      );
                    })()}
                  </ViewModeSetRow>
                ))
              )}

              {isEditMode && (
                <View style={styles.setsFooterRow}>
                  <HoverableTouchableOpacity
                    style={styles.footerActionLink}
                    hoverStyle={styles.footerActionLinkHovered}
                    title={t("addSet-string")}
                    onPress={handleAddSet}
                    onMouseEnter={() => !isMobile && setIsAddSetHovered(true)}
                    onMouseLeave={() => !isMobile && setIsAddSetHovered(false)}
                  >
                    <Text
                      style={[
                        styles.footerActionLinkText,
                        styles.footerActionLinkTextPrimary,
                        isAddSetHovered && { opacity: 0.7 },
                      ]}
                    >
                      + {t("addSet-string")}
                    </Text>
                  </HoverableTouchableOpacity>

                  {!isNotesExpanded && isNotesEmpty && (
                    <View style={styles.footerNoteContainer}>
                      <Tooltip
                        content={t("addNote-string")}
                        placement="top"
                        delayedOpen={true}
                      >
                        <HoverableTouchableOpacity
                          style={styles.footerActionLink}
                          hoverStyle={styles.footerActionLinkHovered}
                          title={t("addNote-string")}
                          onPress={openNotesEditor}
                          onMouseEnter={() =>
                            !isMobile && setIsAddNoteHovered(true)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsAddNoteHovered(false)
                          }
                        >
                          <Text
                            style={[
                              styles.footerActionLinkText,
                              styles.footerActionLinkTextPrimary,
                              isAddNoteHovered && { opacity: 0.7 },
                            ]}
                          >
                            {t("note-string")}
                          </Text>
                        </HoverableTouchableOpacity>
                      </Tooltip>
                    </View>
                  )}
                </View>
              )}
            </View>

            {!isEditMode &&
              typeof notesValue === "string" &&
              notesValue.trim() !== "" && (
                <TouchableOpacity
                  style={styles.exerciseNotesViewRow}
                  activeOpacity={0.9}
                  title={t("editNote-string")}
                  onPress={() => onRequestEditFocus?.("notes")}
                >
                  <View style={styles.exerciseNotesViewBody}>
                    <Text
                      style={[
                        styles.exerciseNotesViewText,
                        isMobile && { fontSize: 11 },
                      ]}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    >
                      {notesValue}
                    </Text>
                  </View>
                </TouchableOpacity>
              )}

            {isEditMode && isNotesExpanded && (
              <View
                style={[styles.exerciseInlineFieldRow, styles.exerciseNotesRow]}
              >
                <View
                  style={[
                    styles.exerciseInlineFieldAccent,
                    { marginVertical: isMobile && isIOS ? 2 : 0 },
                  ]}
                />
                <View style={styles.exerciseInlineFieldBody}>
                  <MobileScaledTextarea
                    className="exercise-notes-textarea"
                    ref={(node) => {
                      if (notesInputRef) {
                        notesInputRef.current = node;
                      }
                    }}
                    value={notesValue}
                    onChange={(e) => {
                      const rawValue = e.target.value ?? "";
                      // Pass value through unchanged - no transformation during typing
                      exercise?.setProperty("notes", rawValue);
                    }}
                    placeholder={t("notesDots-string")}
                    fontSize={isMobile ? 13 : 12}
                    style={{
                      width: "100%",
                      resize: "none",
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      border: "none",
                      outline: "none",
                      fontWeight: "500",
                      color: Colors.darkgrayColor,
                      backgroundColor: "transparent",
                      padding: 0,
                      margin: 0,
                      textAlign: "left",
                      lineHeight: "normal",
                      maxHeight: 100,
                    }}
                    minRows={1}
                    maxLength={300}
                    onFocus={() => setIsNotesFocused(true)}
                    onBlur={(e) => {
                      setIsNotesFocused(false);

                      // Trim only leading and trailing newlines/spaces, preserve internal newlines
                      const currentValue = exercise?.notes ?? "";
                      if (typeof currentValue === "string") {
                        // Remove leading newlines/spaces
                        const trimmedStart = currentValue.replace(
                          /^[\r\n\s]+/,
                          ""
                        );
                        // Remove trailing newlines/spaces
                        const trimmedEnd = trimmedStart.replace(
                          /[\r\n\s]+$/,
                          ""
                        );

                        // Only update if value actually changed
                        if (trimmedEnd !== currentValue) {
                          exercise?.setProperty("notes", trimmedEnd);
                        }
                      }
                    }}
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </View>
              </View>
            )}
          </>
        )}
      </View>
    );

    const modal = isDuplicateExerciseModalVisible ? (
      <DuplicateExerciseModal
        isVisible={isDuplicateExerciseModalVisible}
        onClose={() => setIsDuplicateExerciseModalVisible(false)}
        exercise={exercise}
        block={block}
      />
    ) : null;

    const actualDataWarning = exercise?.showActualWithDataWarning ? (
      <CustomAlert
        isVisible={exercise?.showActualWithDataWarning}
        onClose={() =>
          exercise?.setProperty("showActualWithDataWarning", false)
        }
        title={"actualWithDataProtocolWarningTitle-string"}
        text={"actualWithDataProtocolWarningText-string"}
        action={() => {
          exercise?.setProperty("ignoreActualWithDataWarning", true);
          exercise?.setProperty("showActualWithDataWarning", false);
        }}
        specialCTA={"modify-string"}
      />
    ) : null;

    const linkSlotModal =
      showingSlotLinkView && exercise?.workout ? (
        <LinkSlotView
          isVisible={showingSlotLinkView}
          exercise={exercise}
          onClose={() => setShowingSlotLinkView(false)}
        />
      ) : null;

    const deleteSlotLinkAlert = showingDeleteSlotLinkView ? (
      <CustomAlert
        isVisible={showingDeleteSlotLinkView}
        onClose={() => setShowingDeleteSlotLinkView(false)}
        title={"unlinkExerciseFromSlotWarningTitle-string"}
        text={
          !exercise?.isSecondaryProtocol && exercise?.getSecondProtocol
            ? t("unlinkExercisesFromSlotWarningText-string", {
                firstExerciseName: exercise.getName(t, i18n.language),
                secondExerciseName: exercise.getSecondProtocol.getName(
                  t,
                  i18n.language
                ),
                slotName: t(exercise.protocolSlot),
              })
            : t("unlinkExerciseFromSlotWarningText-string", {
                exerciseName: exercise?.getName?.(t, i18n.language) || "",
                slotName: exercise?.protocolSlot
                  ? t(exercise.protocolSlot)
                  : "",
              })
        }
        action={deleteSlot}
        specialCTA={"unlink-string"}
      />
    ) : null;

    const deleteExerciseAlert = showDeleteExerciseAlert ? (
      <CustomAlert
        isVisible={showDeleteExerciseAlert}
        onClose={() => setShowDeleteExerciseAlert(false)}
        title={"deleteExerciseAlertTitle-string"}
        text={t("deleteExerciseAlertText-string", {
          itemName:
            exercise?.getName?.(t, i18n.language) || t("exercise-string"),
        })}
        action={handleDeleteExercise}
        specialCTA={"delete-string"}
      />
    ) : null;

    const pasteSetsConfirmationAlert = showPasteSetsConfirmationAlert ? (
      <CustomAlert
        isVisible={showPasteSetsConfirmationAlert}
        onClose={() => setShowPasteSetsConfirmationAlert(false)}
        title={"pasteSetsConfirmationTitle-string"}
        text={t("pasteSetsConfirmationMessage-string")}
        action={handleConfirmPasteSets}
        specialCTA={"replace-string"}
      />
    ) : null;

    if (isEditMode)
      return (
        <>
          {cardBody}
          {modal}
          {actualDataWarning}
          {linkSlotModal}
          {deleteSlotLinkAlert}
          {deleteExerciseAlert}
          {pasteSetsConfirmationAlert}
        </>
      );

    return (
      <>
        <TouchableOpacity
          activeOpacity={1}
          title={t("editWorkout-string")}
          onPress={() => onPressCard?.()}
        >
          {cardBody}
        </TouchableOpacity>
        {modal}
        {actualDataWarning}
        {linkSlotModal}
        {deleteSlotLinkAlert}
        {deleteExerciseAlert}
        {pasteSetsConfirmationAlert}
      </>
    );
  }
);

export default ExerciseCard;

const styles = StyleSheet.create({
  exerciseCard: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "transparent",
    outlineStyle: "none",
    gap: 8,
  },
  exerciseCardHovered: {
    borderColor: Colors.borderColor,
  },
  exerciseHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 0,
    borderRadius: 10,
  },
  exerciseHeaderHovered: {},
  exerciseMoveHandle: {
    padding: 2,
  },
  exerciseTitleBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  exerciseNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  inlineEditButton: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 6,
    outlineStyle: "none",
  },
  exerciseName: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    flexShrink: 1,
    minWidth: 0,
  },
  exerciseProtocolSlotRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  exerciseProtocolSlotText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
    flexShrink: 1,
  },
  exerciseProtocolSlotBadge: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  exerciseHeaderActions: {
    flexDirection: "row",
    gap: 5,
    position: "relative",
  },
  iconButton: {
    padding: 4,
  },
  linkIconContainer: {
    position: "relative",
  },
  linkDotOverlay: {
    position: "absolute",
    bottom: 3,
    right: 3,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.bubbleColor,
    borderWidth: 1,
    borderColor: Colors.whiteColor,
  },
  exerciseInlineFieldRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 0,
  },
  exerciseInlineFieldAccent: {
    width: 3,
    alignSelf: "stretch",
    backgroundColor: Colors.grayColor,
    borderRadius: 4,
    marginRight: 4,
    marginTop: 2,
    marginBottom: 2,
  },
  exerciseInlineFieldBody: {
    flex: 1,
    overflow: "hidden",
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    // borderWidth: 1,
  },
  exerciseInlineTextInput: {
    width: "100%",
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    backgroundColor: "transparent",
    paddingVertical: 0,
    paddingHorizontal: 0,
    minHeight: 18,
    lineHeight: 18,
    borderWidth: 0,
    outlineStyle: "none",
    outlineWidth: 0,
    boxShadow: "none",
  },
  exerciseInlineText: {
    width: "100%",
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
    minHeight: 18,
    lineHeight: 18,
  },
  exerciseInlineNotesInput: {
    resize: "none",
    border: "none",
    outline: "none",
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: 18,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  exerciseNotesRow: {
    marginTop: 0,
  },
  exerciseNotesViewRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingHorizontal: 0,
    marginTop: 0,
  },
  exerciseNotesViewBody: {
    flex: 1,
    overflow: "hidden",
  },
  exerciseNotesViewText: {
    width: "100%",
    fontSize: 11,
    fontWeight: "400",
    fontStyle: "italic",
    color: Colors.grayColor,
    lineHeight: 15,
  },
  setsTable: {
    gap: 4,
  },
  tableHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 0,
    width: "100%",
  },
  tableHeaderCameraSpacer: {
    width: 24, // Fixed width to match camera icon column
    alignItems: "center",
  },
  tableHeaderCameraCell: {
    width: 24,
    alignItems: "center",
  },
  tableHeaderPrioritySpacer: {
    width: 24, // Fixed width to match priority icon column
    alignItems: "center",
  },
  tableHeaderPriorityCell: {
    width: 24,
    alignItems: "center",
  },
  tableHeaderPriorityText: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
    textAlign: "center",
  },
  tableHeaderText: {
    width: BlockPlanificationConstants.VALUE_PILL_WIDTH,
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
    textAlign: "center",
  },
  tableHeaderActualText: {
    // flex: 1,
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
    textAlign: "right",
    paddingRight: 6,
  },
  tableActionsSpacer: {
    width: 42,
    marginLeft: "auto",
  },
  actualCell: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "center",
    minWidth: 0,
    paddingRight: 6,
    marginLeft: -4,
  },
  actualText: {
    fontSize: 11.5,
    color: Colors.darkgrayColor,
    fontWeight: "500",
    flexShrink: 1,
    minWidth: 0,
  },
  actualTextMobile: {
    fontSize: 10.5,
  },
  valuePill: {
    width: BlockPlanificationConstants.VALUE_PILL_WIDTH,
    height: BlockPlanificationConstants.VALUE_PILL_HEIGHT,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    overflow: "hidden",
  },
  valuePillViewMode: {
    borderWidth: 0,
  },
  valueText: {
    fontSize: 11.5,
    color: Colors.darkgrayColor,
    fontWeight: "500",
    flexShrink: 1,
    minWidth: 0,
    textAlign: "center",
  },
  valueTextMobile: {
    fontSize: 11.5,
  },
  setDeleteBtn: {
    width: 18,
    height: BlockPlanificationConstants.VALUE_PILL_HEIGHT,
    alignItems: "center",
    justifyContent: "center",
  },
  setCopyBtn: {
    width: 18,
    height: BlockPlanificationConstants.VALUE_PILL_HEIGHT,
    alignItems: "center",
    justifyContent: "center",
  },
  setsFooterRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    paddingTop: 4,
    gap: 8,
  },
  footerActionLink: {
    paddingVertical: 2,
    paddingHorizontal: 0,
  },
  footerActionLinkHovered: {
    opacity: 0.6,
  },
  footerNoteContainer: {
    marginLeft: "auto",
  },
  footerActionDivider: {
    width: 1,
    height: 10,
    backgroundColor: Colors.borderColor,
    opacity: 0.4,
  },
  footerActionLinkText: {
    fontSize: 11,
    color: Colors.grayColor,
    fontWeight: "400",
  },
  footerActionLinkTextPrimary: {
    color: Colors.grayColor,
  },
  moreMenuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 8,
  },
  moreMenuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
});
